import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  fileDialogStore,
  useFileDialog,
  type FileDialogFilter,
} from "../os/file-dialog";
import { createIconsApi } from "../os/icons-host";

const icons = createIconsApi();

function authHeaders(): HeadersInit {
  const t = localStorage.getItem("arclog.token");
  return t ? { Authorization: "Bearer " + t } : {};
}

type Entry = {
  name: string;
  path: string;
  type: "file" | "dir";
  size?: number;
};

function parentPath(p: string) {
  if (!p || p === "/") return "/";
  const parts = p.replace(/\/+$/, "").split("/");
  parts.pop();
  return parts.join("/") || "/";
}

function matchesFilter(name: string, filters?: FileDialogFilter[], active = 0) {
  if (!filters?.length) return true;
  const f = filters[Math.max(0, Math.min(active, filters.length - 1))];
  if (!f || f.extensions.includes("*")) return true;
  const lower = name.toLowerCase();
  return f.extensions.some((ext) => lower.endsWith("." + ext.toLowerCase().replace(/^\./, "")));
}

export function FileDialogHost() {
  const dlg = useFileDialog();
  const [path, setPath] = useState("/");
  const [entries, setEntries] = useState<Entry[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [filename, setFilename] = useState("");
  const [filterIdx, setFilterIdx] = useState(0);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!dlg) return;
    setPath(dlg.startPath || "/");
    setFilename(dlg.filename || "");
    setSelected(null);
    setFilterIdx(0);
    setError("");
  }, [dlg?.id]);

  useEffect(() => {
    if (!dlg) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await fetch(
          `/api/fs/list?path=${encodeURIComponent(path)}`,
          { headers: authHeaders() }
        );
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || res.statusText);
        if (!cancelled) {
          const list = (data.entries || []) as Entry[];
          list.sort((a, b) => {
            if (a.type !== b.type) return a.type === "dir" ? -1 : 1;
            return a.name.localeCompare(b.name);
          });
          setEntries(list);
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [dlg?.id, path]);

  useEffect(() => {
    if (!dlg) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        fileDialogStore.dismiss();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [dlg?.id]);

  if (!dlg) return null;

  const isFolder = dlg.mode === "open-folder";
  const isSave = dlg.mode === "save-file";

  const visible = entries.filter((e) => {
    if (e.type === "dir") return true;
    if (isFolder) return false;
    return matchesFilter(e.name, dlg.filters, filterIdx);
  });

  const crumbs = (() => {
    const parts = path.split("/").filter(Boolean);
    const out: { label: string; path: string }[] = [{ label: "root", path: "/" }];
    let acc = "";
    for (const p of parts) {
      acc += "/" + p;
      out.push({ label: p, path: acc });
    }
    return out;
  })();

  const choose = () => {
    if (isFolder) {
      fileDialogStore.settle(selected && entries.find((e) => e.path === selected && e.type === "dir")
        ? selected
        : path);
      return;
    }
    if (isSave) {
      const name = filename.trim();
      if (!name) {
        setError("Enter a file name");
        return;
      }
      const full = path.replace(/\/+$/, "") + "/" + name.replace(/^\/+/, "");
      fileDialogStore.settle(full);
      return;
    }
    // open file
    if (!selected) {
      setError("Select a file");
      return;
    }
    const ent = entries.find((e) => e.path === selected);
    if (!ent || ent.type !== "file") {
      setError("Select a file");
      return;
    }
    fileDialogStore.settle(selected);
  };

  const openEntry = (e: Entry) => {
    if (e.type === "dir") {
      setPath(e.path);
      setSelected(null);
    } else if (!isFolder) {
      setSelected(e.path);
      if (isSave) setFilename(e.name);
      else fileDialogStore.settle(e.path);
    }
  };

  const node = (
    <div
      className="os-dialog-backdrop"
      onMouseDown={(ev) => {
        if (ev.target === ev.currentTarget) fileDialogStore.dismissFromBackdrop();
      }}
      onClick={(ev) => {
        if (ev.target === ev.currentTarget) fileDialogStore.dismissFromBackdrop();
      }}
    >
      <div
        className="os-dialog glass glass-strong os-file-dialog"
        role="dialog"
        aria-modal="true"
        aria-label={dlg.title}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="os-dialog-title">{dlg.title}</div>
        <div className="os-fd-crumbs">
          {crumbs.map((c, i) => (
            <button
              key={c.path + i}
              type="button"
              className="os-fd-crumb"
              onClick={() => {
                setPath(c.path);
                setSelected(null);
              }}
            >
              {c.label}
            </button>
          ))}
        </div>
        <div className="os-fd-toolbar">
          <button
            type="button"
            className="os-fd-tool"
            title="Up"
            onClick={() => {
              setPath(parentPath(path));
              setSelected(null);
            }}
          >
            ↑
          </button>
          <div className="os-fd-path">{path}</div>
        </div>
        <div className="os-fd-list">
          {loading && <div className="os-fd-empty">Loading…</div>}
          {!loading && !visible.length && (
            <div className="os-fd-empty">Empty folder</div>
          )}
          {visible.map((e) => {
            const on = selected === e.path;
            return (
              <button
                key={e.path}
                type="button"
                className={"os-fd-row" + (on ? " on" : "")}
                onClick={() => {
                  setSelected(e.path);
                  if (e.type === "file" && isSave) setFilename(e.name);
                }}
                onDoubleClick={() => openEntry(e)}
              >
                <span
                  className="os-fd-ico"
                  ref={(node) => {
                    if (!node || node.childNodes.length) return;
                    try {
                      node.appendChild(
                        icons.forEntry(
                          { name: e.name, type: e.type },
                          { size: 16 }
                        )
                      );
                    } catch {
                      /* */
                    }
                  }}
                />
                <span className="os-fd-name">{e.name}</span>
                <span className="os-fd-meta">
                  {e.type === "dir" ? "Folder" : ""}
                </span>
              </button>
            );
          })}
        </div>
        {isSave && (
          <div className="os-fd-save">
            <label>Name</label>
            <input
              value={filename}
              onChange={(e) => setFilename(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  choose();
                }
              }}
            />
          </div>
        )}
        {!!dlg.filters?.length && !isFolder && (
          <div className="os-fd-filters">
            <label>Format</label>
            <select
              value={filterIdx}
              onChange={(e) => setFilterIdx(Number(e.target.value))}
            >
              {dlg.filters.map((f, i) => (
                <option key={f.label + i} value={i}>
                  {f.label} ({f.extensions.map((x) => (x === "*" ? "*" : "." + x)).join(", ")})
                </option>
              ))}
            </select>
          </div>
        )}
        {error && <div className="os-fd-error">{error}</div>}
        <div className="os-dialog-actions">
          <button type="button" className="os-btn ghost" onClick={() => fileDialogStore.dismiss()}>
            Cancel
          </button>
          <button type="button" className="os-btn primary" onClick={choose}>
            {isSave ? "Save" : isFolder ? "Select Folder" : "Open"}
          </button>
        </div>
      </div>
    </div>
  );
  return createPortal(node, document.body);
}
